import {
  EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS,
  SoftwareTypeSchema,
  type DomainListQueryDto,
  type ExternalAssetListOrderByDto,
  type ExternalNetworkEntityKind,
  type GatewayListQueryDto,
  type HostListQueryDto,
  type NetBlockListQueryDto,
  type NetworkServiceListQueryDto,
  type SoftwareListQueryDto,
  type StorageServiceListQueryDto,
} from '@terra/shared-contracts-external-network';

export type ExternalNetworkAssetListFilters = {
  product?: string;
  softwareType?: string;
  cveId?: string;
  region?: string;
  organization?: string;
  createdStartDate?: Date;
  createdEndDate?: Date;
  scope?: 'IN_SCOPE' | 'OUT_OF_SCOPE';
};

export function kindSupportsAllAssetFilters({
  entityKind,
  filters,
}: {
  entityKind: ExternalNetworkEntityKind;
  filters?: ExternalNetworkAssetListFilters;
}): boolean {
  const hasProduct = Boolean(filters?.product);
  const hasCve = Boolean(filters?.cveId);
  const hasTechnology = Boolean(filters?.softwareType);
  const hasRegion = Boolean(filters?.region);
  const hasOrganization = Boolean(filters?.organization);
  const hasScope = Boolean(filters?.scope);

  switch (entityKind) {
    case 'DOMAIN':
      return !hasRegion && !hasOrganization;
    case 'HOST':
    case 'NETWORK_SERVICE':
      return !hasRegion && !hasOrganization;
    case 'SOFTWARE':
      return !hasRegion && !hasOrganization && !hasScope;
    case 'GATEWAY':
      return !hasProduct && !hasCve && !hasRegion && !hasOrganization;
    case 'STORAGE_SERVICE':
      return !hasProduct && !hasCve && !hasTechnology && !hasOrganization;
    case 'NETBLOCK':
      return !hasProduct && !hasCve && !hasTechnology;
  }
}

export type ExternalNetworkAssetListQuery =
  | DomainListQueryDto
  | HostListQueryDto
  | GatewayListQueryDto
  | NetworkServiceListQueryDto
  | NetBlockListQueryDto
  | SoftwareListQueryDto
  | StorageServiceListQueryDto;

function createdAtQuery(filters: ExternalNetworkAssetListFilters) {
  return {
    ...(filters.createdStartDate
      ? { createdStartDate: filters.createdStartDate }
      : {}),
    ...(filters.createdEndDate
      ? { createdEndDate: filters.createdEndDate }
      : {}),
  };
}

function parsedSoftwareType(softwareType: string | undefined) {
  const parsed = SoftwareTypeSchema.safeParse(softwareType);

  return parsed.success ? parsed.data : undefined;
}

function softwareRelationQuery(filters: ExternalNetworkAssetListFilters) {
  const softwareType = parsedSoftwareType(filters.softwareType);

  return {
    ...(filters.product ? { product: filters.product } : {}),
    ...(softwareType ? { softwareType } : {}),
    ...(filters.cveId ? { cveId: filters.cveId } : {}),
  };
}

type BuildAssetListQueryArgs<K extends ExternalNetworkEntityKind> = {
  entityKind: K;
  search?: string;
  skip?: number;
  limit: number;
  filters?: ExternalNetworkAssetListFilters;
  orderBy?: ExternalAssetListOrderByDto[];
  groupBy?: string;
};

function pickGroupBy<Field extends string>({
  fields,
  groupBy,
}: {
  fields: readonly Field[];
  groupBy: string | undefined;
}): { groupBy?: Field } {
  const field = fields.find((candidate) => candidate === groupBy);

  return field ? { groupBy: field } : {};
}

type BuildExternalNetworkAssetListQuery = {
  (args: BuildAssetListQueryArgs<'DOMAIN'>): DomainListQueryDto;
  (args: BuildAssetListQueryArgs<'HOST'>): HostListQueryDto;
  (args: BuildAssetListQueryArgs<'GATEWAY'>): GatewayListQueryDto;
  (
    args: BuildAssetListQueryArgs<'NETWORK_SERVICE'>,
  ): NetworkServiceListQueryDto;
  (args: BuildAssetListQueryArgs<'NETBLOCK'>): NetBlockListQueryDto;
  (args: BuildAssetListQueryArgs<'SOFTWARE'>): SoftwareListQueryDto;
  (
    args: BuildAssetListQueryArgs<'STORAGE_SERVICE'>,
  ): StorageServiceListQueryDto;
};

function buildAssetListQuery({
  entityKind,
  search,
  skip = 0,
  limit,
  filters = {},
  orderBy,
  groupBy,
}: BuildAssetListQueryArgs<ExternalNetworkEntityKind>): ExternalNetworkAssetListQuery {
  const base = {
    skip,
    limit,
    ...(search?.trim() ? { search: search.trim() } : {}),
    ...(orderBy && orderBy.length > 0 ? { orderBy } : {}),
    ...createdAtQuery(filters),
  };

  switch (entityKind) {
    case 'DOMAIN':
      return {
        ...base,
        ...pickGroupBy({
          fields: EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS.DOMAIN,
          groupBy,
        }),
        ...softwareRelationQuery(filters),
        ...(filters.scope ? { scope: filters.scope } : {}),
      };
    case 'HOST':
      return {
        ...base,
        ...pickGroupBy({
          fields: EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS.HOST,
          groupBy,
        }),
        ...softwareRelationQuery(filters),
        ...(filters.scope ? { scope: filters.scope } : {}),
      };
    case 'NETWORK_SERVICE':
      return {
        ...base,
        ...pickGroupBy({
          fields: EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS.NETWORK_SERVICE,
          groupBy,
        }),
        ...softwareRelationQuery(filters),
        ...(filters.scope ? { scope: filters.scope } : {}),
      };
    case 'SOFTWARE':
      return {
        ...base,
        ...pickGroupBy({
          fields: EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS.SOFTWARE,
          groupBy,
        }),
        ...softwareRelationQuery(filters),
      };
    case 'GATEWAY': {
      const softwareType = parsedSoftwareType(filters.softwareType);

      return {
        ...base,
        ...pickGroupBy({
          fields: EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS.GATEWAY,
          groupBy,
        }),
        ...(softwareType ? { softwareType } : {}),
        ...(filters.scope ? { scope: filters.scope } : {}),
      };
    }

    case 'NETBLOCK':
      return {
        ...base,
        ...pickGroupBy({
          fields: EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS.NETBLOCK,
          groupBy,
        }),
        ...(filters.region ? { region: filters.region } : {}),
        ...(filters.organization ? { organization: filters.organization } : {}),
        ...(filters.scope ? { scope: filters.scope } : {}),
      };
    case 'STORAGE_SERVICE':
      return {
        ...base,
        ...pickGroupBy({
          fields: EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS.STORAGE_SERVICE,
          groupBy,
        }),
        ...(filters.region ? { region: filters.region } : {}),
        ...(filters.scope ? { scope: filters.scope } : {}),
      };
  }
}

export const buildExternalNetworkAssetListQuery =
  buildAssetListQuery as BuildExternalNetworkAssetListQuery;

export function assetListFilterQueryKey(
  filters: ExternalNetworkAssetListFilters,
) {
  return [
    filters.product ?? null,
    filters.softwareType ?? null,
    filters.cveId ?? null,
    filters.region ?? null,
    filters.organization ?? null,
    filters.createdStartDate?.toISOString() ?? null,
    filters.createdEndDate?.toISOString() ?? null,
    filters.scope ?? null,
  ];
}
